import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { token } = useContext(AuthContext);
  const [quotas, setQuotas] = useState(null);

  const fetchQuotas = useCallback(async () => {
    if (!token) {
      setQuotas(null);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/quotas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotas(response.data);
    } catch (err) {
      setQuotas(null);
    }
  }, [token]);

  useEffect(() => {
    fetchQuotas();
  }, [fetchQuotas]);

  const isFree = quotas?.is_free ?? true;
  const transactionsUsed = quotas?.limits?.transactions?.used ?? 0;
  const transactionsMax = quotas?.limits?.transactions?.max;
  const assetsUsed = quotas?.limits?.assets?.used ?? 0;
  const assetsMax = quotas?.limits?.assets?.max;
  const features = quotas?.premium_features ?? {};

  const canAddTransaction = !isFree || transactionsUsed < (transactionsMax || 0);
  const canAddAsset = !isFree || assetsUsed < (assetsMax || 0);

  const value = useMemo(() => ({
    quotas,
    isFree,
    transactionsUsed,
    transactionsMax,
    assetsUsed,
    assetsMax,
    canAddTransaction,
    canAddAsset,
    features,
    refresh: fetchQuotas,
  }), [quotas, isFree, transactionsUsed, transactionsMax, assetsUsed, assetsMax, canAddTransaction, canAddAsset, features, fetchQuotas]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
